import { ABIDescription, IRemixApi } from '@remixproject/plugin-api'
import { PluginClient } from '@remixproject/plugin'
import { PluginApi } from '@remixproject/plugin-utils'
import { BigNumberish, Contract, ContractFactory, Interface, JsonRpcSigner, keccak256, Wallet } from "ethers"

import { ACCOUNT_ONBOARD_ABI, ACCOUNT_ONBOARD_ADDRESS } from './constants';
import { generateRSAKeyPair, recoverUserKey, sign } from '@coti-io/coti-sdk-typescript';
import { formatAddress } from './helpers';

export const deployContract = async (
    client: PluginClient<any, Readonly<IRemixApi>> & PluginApi<Readonly<IRemixApi>>,
    signer: JsonRpcSigner | Wallet,
    contractName: string,
    contractAbi: ABIDescription[],
    contractBytecode: string,
    constructorArgs: any[],
    txValue: bigint,
    gasLimit: BigNumberish
) => {
    const factory = new ContractFactory(contractAbi, contractBytecode, signer)

    await client.terminal.log({
        type: 'log',
        value: `creation of ${contractName} pending...`
    })

    try {
        const txOptions = gasLimit === '0' ? { value: txValue } : { value: txValue, gasLimit: gasLimit }

        let contract = await factory
            .deploy(...constructorArgs, txOptions)
        
        const res = await contract.waitForDeployment()

        const tx = res.deploymentTransaction()

        if (!tx) {
            throw new Error('deployment tx not found')
        }

        await client.terminal.log({
            type: 'info',
            value: `from: ${formatAddress(tx.from)} \nto: ${contractName}.(constructor) \nvalue: ${txValue} wei \ndata: ${formatData(tx.data)} \nhash: ${tx.hash}`
        })

        return res.target
    } catch (e) {
        await client.terminal.log({
            type: 'error',
            value: `${e}`
        })

        return ''
    }
}

export const onboardUser = async (
    client: PluginClient<any, Readonly<IRemixApi>> & PluginApi<Readonly<IRemixApi>>,
    signer: JsonRpcSigner | Wallet
) => {
    const accountOnboardContract = new Contract(ACCOUNT_ONBOARD_ADDRESS, ACCOUNT_ONBOARD_ABI, signer)

    const rsaKeyPair = generateRSAKeyPair()
    
    try {
        let signedEK: Uint8Array | string
    
        if (signer instanceof JsonRpcSigner) {
            signedEK = await signer.signMessage(rsaKeyPair.publicKey)
        } else {
            signedEK = sign(keccak256(rsaKeyPair.publicKey), signer.privateKey)
        }
    
        await client.terminal.log({
            type: 'log',
            value: 'transact to AccountOnboard.onboardAccount pending...'
        })

        let tx = await accountOnboardContract.onboardAccount(
            rsaKeyPair.publicKey,
            signedEK
        )
    
        const receipt = await tx.wait()
    
        tx = await signer.provider?.getTransaction(receipt.hash)
    
        await client.terminal.log({
            type: 'info',
            value: `from: ${formatAddress(tx.from)} \nto: AccountOnboard.onboardAccount \nvalue: 0 wei \ndata: ${formatData(tx.data)} \nhash: ${tx.hash}`
        })
    
        if (!receipt || !receipt.logs || !receipt.logs[0]) {
            throw new Error("failed to onboard account")
        }
    
        const decodedLog = accountOnboardContract.interface.parseLog(receipt.logs[0])
    
        if (!decodedLog) {
            throw new Error("failed to decode logs")
        }
    
        const encryptedKey1 = decodedLog.args.userKey1
        const encryptedKey2 = decodedLog.args.userKey2
    
        return recoverUserKey(rsaKeyPair.privateKey, encryptedKey1.substring(2), encryptedKey2.substring(2))
    } catch (e) {
        await client.terminal.log({
            type: 'error',
            value: `${e}`
        })

        return ''
    }
}

export const executeTransaction = async (
    client: PluginClient<any, Readonly<IRemixApi>> & PluginApi<Readonly<IRemixApi>>,
    signer: JsonRpcSigner | Wallet,
    contractName: string,
    contractAddress: string,
    contractAbi: ABIDescription[],
    functionName: string,
    args: any[],
    txValue: bigint,
    gasLimit: BigNumberish
) => {
    const contract = new Contract(contractAddress, contractAbi, signer)

    await client.terminal.log({
        type: 'log',
        value: `transact to ${contractName}.${functionName} pending...`
    })

    try {
        const txOptions = gasLimit === '0' ? { value: txValue } : { value: txValue, gasLimit: gasLimit }

        let tx = await contract[functionName](...args, txOptions)

        const receipt = await tx.wait()

        tx = await signer.provider?.getTransaction(receipt.hash)

        await client.terminal.log({
            type: 'info',
            value: `from: ${formatAddress(tx.from)} \nto: ${contractName}.${functionName} \nvalue: ${txValue} wei \ndata: ${formatData(tx.data)} \nhash: ${tx.hash}`
        })
    } catch (e) {
        await client.terminal.log({
            type: 'error',
            value: `${e}`
        })
    }
}

export const executeCall = async (
    client: PluginClient<any, Readonly<IRemixApi>> & PluginApi<Readonly<IRemixApi>>,
    signer: JsonRpcSigner | Wallet,
    contractName: string,
    contractAddress: string,
    contractAbi: ABIDescription[],
    functionName: string,
    args: any[]
) => {
    const iface = new Interface(contractAbi)
    const data = iface.encodeFunctionData(functionName, args)

    await client.terminal.log({
        type: 'log',
        value: `call to ${contractName}.${functionName}`
    })

    try {
        const res = await signer.call({
            to: contractAddress,
            data: data,
            gasLimit: 15000000
        })

        const decoded = iface.decodeFunctionResult(functionName, res)

        await client.terminal.log({
            type: 'info',
            value: `from: ${formatAddress(signer.address)} \nto: ${contractName}.${functionName} \ndata: ${formatData(data)}`
        })
    
        return decoded
    } catch (e) {
        await client.terminal.log({
            type: 'error',
            value: `${e}`
        })

        return ''
    }
}

const formatData = (data: string) => {
    if (data.length === 10) {
        return data
    }

    return `${data.substring(0, 10)}...${data.substring(data.length - 8)}`
}